import { Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { combineLatest, forkJoin, Observable, Subject, Subscription } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { ToastButtons, ToastResponse } from '../Common/toast';
import { ToastService } from '../Common/toast/toast.service';
import { ToastMessageType } from '../Common/toast/ToastMessageType';
import { TenantGroups } from '../Enums';
import { Permissions } from '../Models/Permissions';
import { TenantGroupPermission } from '../Models/tenant-group-permission.model';
import { TenantService } from '../Services';
import { Feature } from './feature.model';
import { FeatureManagementDataService } from './featureManagement-data.service';
import { GroupFeatureViewModel, UserGroup } from './userGroup.model';
import { MatSidenavContainer, MatSidenav } from '@angular/material/sidenav';
import { MatNavList, MatListItem } from '@angular/material/list';
import { RouterLinkActive } from '@angular/router';
import { NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'my-package',
    templateUrl: './featureManagement.component.html',
    styleUrls: ['featureManagement.component.less'],
    providers: [Title],
    imports: [MatSidenavContainer, MatSidenav, MatNavList, MatListItem, RouterLinkActive, NgClass, MatIcon, MatSlideToggle, FormsModule]
})
export class FeatureManagementComponent implements OnInit, OnDestroy {
  public selectedUserGroup: UserGroup;
  public show: boolean;
  public groups: UserGroup[];
  public groupFeatures: GroupFeatureViewModel[];
  public tenantGroups: TenantGroupPermission[];
  public features: Feature[];
  public selectedGroupFeatures: GroupFeatureViewModel[];
  public selectedTenantGroups: TenantGroupPermission[];
  public newGroup: UserGroup = new UserGroup();
  private deleteGroup$: Subject<UserGroup>;
  private isDestroyed$: Subject<void> = new Subject();
  private subscriptions: Subscription[] = [];
  private selectedUserGroupHasAdmin = false;
  private selectedUserGroupHasMaster = false;
  private readonly permissions = Permissions;

  constructor(
    private title: Title,
    private dataService: FeatureManagementDataService,
    private tenantService: TenantService,
    private toastService: ToastService
  ) {
    this.title.setTitle('ACT - Feature Management');
  }

  public getTenantGroupName(tenantGroupAlias: string): TenantGroups {
        return TenantGroups[tenantGroupAlias];
  }

  public ngOnInit(): void {
    this.setGroups();
    this.setFeatures();
    this.setGroupFeatures();
    this.setTenantGroups();
    this.handleDeletingGroups();
  }

  public open(userGroup: UserGroup): void {
    this.selectedUserGroup = userGroup;
    this.selectedGroupFeatures = this.getGroupFeatures(userGroup);
    this.selectedTenantGroups = this.getTenantGroupsForUserGroup(userGroup);
  }

  public isDisabledToggle(groupFeature: GroupFeatureViewModel): boolean {
    const thisFeatureIsNotAdmin =
      groupFeature.FeatureName !== this.permissions.admin;
    const shouldGrantWithAdmin = this.features.find(
      (feature) => feature.Name === groupFeature.FeatureName
    ).GrantedAutomaticallyWithAdmin;
    return (
      thisFeatureIsNotAdmin &&
      this.selectedUserGroupHasAdmin &&
      shouldGrantWithAdmin
    );
  }

  public isDisabledTenantGroup(groupFeature: TenantGroupPermission): boolean {
    const thisFeatureIsNotMaster =
      groupFeature.tenantGroupName !== TenantGroups.Master;
    return thisFeatureIsNotMaster && this.selectedUserGroupHasMaster;
  }

  public toggleFeatureForGroup(groupFeature: GroupFeatureViewModel): void {
    if (groupFeature.FeatureName === this.permissions.admin) {
      this.selectedUserGroupHasAdmin = groupFeature.HasPermission;
    }

    const updatesRequired: Observable<object>[] =
      this.updateGroupFeature(groupFeature);

    forkJoin(updatesRequired)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe(() => this.setGroupFeatures());
  }

  public toggleTenantGroupForGroup(tenantGroup: TenantGroupPermission): void {
    if (tenantGroup.tenantGroupName === TenantGroups.Master) {
      this.selectedUserGroupHasMaster = tenantGroup.hasPermission;
    }
    const updatesRequired: Observable<void>[] =
      this.updateTenantGroup(tenantGroup);
    this.subscriptions.push(
      forkJoin(updatesRequired).subscribe(() => this.setTenantGroups())
    );
  }

  public deleteGroup(selectedUserGroup: UserGroup): void {
    this.deleteGroup$.next(selectedUserGroup);
  }

  public onSubmit(): void {
    this.addGroup(this.newGroup.Id, this.newGroup.Name)
      .pipe(take(1))
      .subscribe(() => {
        this.toastService.sendMessage(
          'Saved',
          'Successfully saved ' + name,
          ToastMessageType.success
        );
        this.hideAddGroup();
        this.setGroups();
      });
  }

  public addGroup(id: string, name: string): Observable<any> {
    return this.dataService.addGroup(name, id);
  }

  public showAddGroup(): void {
    this.show = true;
    this.newGroup = new UserGroup();
  }

  public hideAddGroup(): void {
    this.show = false;
    this.newGroup = new UserGroup();
  }

  public isUserGroupSelected(userGroup: UserGroup): boolean {
    if (!this.selectedUserGroup) {
      return false;
    }
    return this.selectedUserGroup.Id === userGroup.Id;
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.isDestroyed$.next();
  }

  private handleDeletingGroups(): void {
    this.deleteGroup$ = new Subject<UserGroup>();
    const deleteGroupSubscription = this.deleteGroup$
      .pipe(
        takeUntil(this.isDestroyed$),
        // using exhaust map
        // this is used when you don't want someone to
        // keep mashing the delete button.
        // it will simply ignore these until
        // they have closed the message dialog.
        // this stops multiple toasts appearing.
        exhaustMap((selectedGroup) => {
          const message = this.toastService.sendMessage(
            'Delete Group ' + selectedGroup.Name,
            'Are you sure you want to delete this group?',
            ToastMessageType.warning,
            ToastButtons.YesNo,
            0
          );
          return combineLatest([message]).pipe(
            map((result) => [result[0], selectedGroup])
          );
        }),
        switchMap(([result, selectedGroup]: [ToastResponse, UserGroup]) => {
          if (result === ToastResponse.Yes) {
            return this.dataService.deleteGroup(selectedGroup.Id).pipe(
              catchError((response) => this.toastService.sendMessage(
                  'Error trying to delete',
                  response.error.ExceptionMessage,
                  ToastMessageType.error
                ))
            );
          }
          return [];
        })
      )
      .subscribe(() => {
        this.setGroups();
        this.setGroupFeatures();
        this.selectedUserGroup = null;
      });
    this.subscriptions.push(deleteGroupSubscription);
  }

  private setGroups(): void {
    this.dataService
      .groups()
      .pipe(take(1))
      .subscribe((groups) => {
        this.groups = groups;
      });
  }

  private setFeatures(): void {
    this.dataService
      .features()
      .pipe(take(1))
      .subscribe((features) => {
        this.features = features;
      });
  }

  private setGroupFeatures(): void {
    this.dataService
      .groupFeatures()
      .pipe(take(1))
      .subscribe((gf) => {
        this.groupFeatures = gf.map(
          (groupFeature) => new GroupFeatureViewModel(groupFeature)
        );
      });
  }

  private setTenantGroups(): void {
    const subscription = this.tenantService
      .getTenantPermissionGroups()
      .subscribe((tenantGroups) => {
        this.tenantGroups = tenantGroups.map(
          (tenantGroupDto) => new TenantGroupPermission(tenantGroupDto)
        );
      });
    this.subscriptions.push(subscription);
  }

  private updateGroupFeature(
    groupFeature: GroupFeatureViewModel
  ): Observable<object>[] {
    if (
      groupFeature.FeatureName === this.permissions.admin &&
      groupFeature.HasPermission
    ) {
      return this.enableAllGroupFeaturesForUserGroup();
    }
    return [this.dataService.setGroupFeature(groupFeature)];
  }

  private enableAllGroupFeaturesForUserGroup(): Observable<object>[] {
    return this.selectedGroupFeatures
      .filter(
        (groupFeature) =>
          this.isFeatureGrantedAutomaticallyWithAdmin(
            groupFeature.FeatureName
          ) && this.isFeatureAdminOrHasNotPermission(groupFeature)
      )
      .map((feature) => {
        feature.HasPermission = true;
        return this.dataService.setGroupFeature(feature);
      });
  }

  private isFeatureAdminOrHasNotPermission(
    groupFeature: GroupFeatureViewModel
  ): boolean {
    return (
      !groupFeature.HasPermission ||
      groupFeature.FeatureName === this.permissions.admin
    );
  }

  private updateTenantGroup(
    tenantGroup: TenantGroupPermission
  ): Observable<void>[] {
    if (
      tenantGroup.tenantGroupName === TenantGroups.Master &&
      tenantGroup.hasPermission
    ) {
      return this.enableAllTenantGroupsForUserGroup();
    }
    return [this.tenantService.setTenantGroupPermission(tenantGroup)];
  }

  private enableAllTenantGroupsForUserGroup(): Observable<void>[] {
    return this.selectedTenantGroups
      .filter(
        (tenantGroup) =>
          !tenantGroup.hasPermission ||
          tenantGroup.tenantGroupName === TenantGroups.Master
      )
      .map((tenantGroup) => {
        tenantGroup.hasPermission = true;
        return this.tenantService.setTenantGroupPermission(tenantGroup);
      });
  }

  private isFeatureGrantedAutomaticallyWithAdmin(featureName: string): boolean {
    return this.features.find((feature) => feature.Name === featureName)
      .GrantedAutomaticallyWithAdmin;
  }

  private getGroupFeatures(group: UserGroup): GroupFeatureViewModel[] {
    this.selectedUserGroupHasAdmin = this.groupFeatures.find(
      (groupFeature) =>
        groupFeature.GroupId === group.Id &&
        groupFeature.FeatureName === this.permissions.admin
    ).HasPermission;
    return this.groupFeatures.filter(
      (groupFeature) => groupFeature.GroupId === group.Id
    );
  }

  private getTenantGroupsForUserGroup(
    group: UserGroup
  ): TenantGroupPermission[] {
    const tenantGroupsForUserGroup = this.tenantGroups.filter(
      (tenantGroup) => tenantGroup.groupId === group.Id
    );
    this.selectedUserGroupHasMaster = tenantGroupsForUserGroup.find(
      (tenantGroup) => tenantGroup.tenantGroupName === TenantGroups.Master
    ).hasPermission;
    return tenantGroupsForUserGroup;
  }

}
